import type { SeriesLineOptions } from 'highcharts';
import { useCallback, useMemo } from 'react';
import { useAvailableMetrics } from '../AggregatedMetric/useAvailableMetrics';
import {
  resolveMetricDisplayName,
  SIGNALS_TIME_RANGE_LIMIT,
  useMetricDisplayNameMap,
  useSignalsAssetMap,
  useSignalsTimeseries,
  type SignalsTimeRange,
} from '../core';

export const DEFAULT_METRIC = 'CapMrktEstUSD';
export const DEFAULT_METRIC_FULL_NAME = 'Estimated Market Cap (Est.)';

interface UseMarketCapDataResult {
  series: SeriesLineOptions[];
  isFetching: boolean;
  isError: boolean;
  /** Talos currency symbols available for this metric, ready for the dropdown. */
  talosSymbols: string[];
  /** Whether the asset catalog + reference translation is still in flight. */
  isAssetsLoading: boolean;
  /** CoinMetrics metric IDs supported by the currently selected asset. */
  availableMetrics: string[];
  isMetricsLoading: boolean;
  isMetricsError: boolean;
  /** Human-readable full name for the selected metric (e.g. `'Estimated Market Cap (Est.)'`). Falls back to the metric ID. */
  metricFullName: string;
  /** Returns the human-readable full name for any metric ID; falls back to the ID itself when reference data is absent. */
  getMetricFullName: (metricId: string) => string;
}

/** Data hook for the Single Asset Metric module. */
export function useMarketCapData(
  selectedSymbol: string | undefined,
  metric: string,
  timeRange: SignalsTimeRange
): UseMarketCapDataResult {
  const metricsForAssetMap = useMemo(() => [metric], [metric]);

  const {
    talosSymbols,
    cmAssetIdByTalosSymbol,
    isLoading: isAssetsLoading,
    isError: isAssetsError,
  } = useSignalsAssetMap(metricsForAssetMap);

  const cmAssetId = selectedSymbol != null ? cmAssetIdByTalosSymbol.get(selectedSymbol) : undefined;

  const {
    series,
    isFetching,
    isError: isTimeseriesError,
  } = useSignalsTimeseries({
    metrics: metricsForAssetMap,
    cmAssetId,
    limitPerAsset: SIGNALS_TIME_RANGE_LIMIT[timeRange],
  });

  // Metrics available for the selected asset — drives the metric dropdown.
  const cmAssetIds = useMemo(() => (cmAssetId != null ? [cmAssetId] : []), [cmAssetId]);
  const {
    metrics: availableMetrics,
    isLoading: isMetricsLoading,
    isError: isMetricsError,
  } = useAvailableMetrics(cmAssetIds);

  // Used to label dropdown options and the chart title/tooltip with human-readable names.
  const { displayNameByMetricId } = useMetricDisplayNameMap();
  const metricFullName = resolveMetricDisplayName(metric, displayNameByMetricId);

  const getMetricFullName = useCallback(
    (metricId: string) => resolveMetricDisplayName(metricId, displayNameByMetricId),
    [displayNameByMetricId]
  );

  return {
    series,
    isFetching,
    isError: isAssetsError || isTimeseriesError,
    talosSymbols,
    isAssetsLoading,
    availableMetrics,
    isMetricsLoading,
    isMetricsError,
    metricFullName,
    getMetricFullName,
  };
}
